import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { BidHistoryItem } from '@/types/bidHistory';
import { BidOutcomeBadge } from './BidOutcomeBadge';
import { SelectionReasonsBadges } from './SelectionReasonsBadges';
import { JOB_CATEGORIES } from '@/lib/types/job';
import { format } from 'date-fns';
import { 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown, 
  ExternalLink,
  Download
} from 'lucide-react';

interface BidHistoryTableProps {
  bids: BidHistoryItem[];
  loading?: boolean;
}

type SortKey = 'submittedAt' | 'jobTitle' | 'totalBidCents' | 'outcome';
type SortDirection = 'asc' | 'desc';

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function getCategoryLabel(category: string): string {
  const found = JOB_CATEGORIES.find(c => c.value === category);
  return found?.label || 'Other';
}

export function BidHistoryTable({ bids, loading }: BidHistoryTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('submittedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const sortedBids = [...bids].sort((a, b) => {
    let comparison = 0;
    
    switch (sortKey) {
      case 'submittedAt':
        comparison = new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime();
        break;
      case 'jobTitle':
        comparison = a.jobTitle.localeCompare(b.jobTitle);
        break;
      case 'totalBidCents':
        comparison = a.totalBidCents - b.totalBidCents;
        break;
      case 'outcome':
        const outcomeOrder = { won: 0, pending: 1, lost: 2, expired: 3 };
        comparison = outcomeOrder[a.outcome] - outcomeOrder[b.outcome];
        break;
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const SortIcon = ({ columnKey }: { columnKey: SortKey }) => {
    if (sortKey !== columnKey) {
      return <ArrowUpDown className="ml-1 h-3 w-3 text-muted-foreground" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="ml-1 h-3 w-3" />
      : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Job Title', 'Client', 'Category', 'Bid Amount', 'Outcome', 'Award Reasons'];
    const rows = sortedBids.map(bid => [
      format(new Date(bid.submittedAt), 'yyyy-MM-dd'),
      bid.jobTitle,
      bid.clientName,
      getCategoryLabel(bid.jobCategory),
      formatCurrency(bid.totalBidCents),
      bid.outcome,
      bid.selectionReasons?.join('; ') || '',
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bid-history-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (bids.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-muted-foreground">No bids found matching your criteria.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={exportToCSV}>
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block">
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('submittedAt')}
                >
                  <div className="flex items-center">
                    Date Submitted
                    <SortIcon columnKey="submittedAt" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('jobTitle')}
                >
                  <div className="flex items-center">
                    Job Title
                    <SortIcon columnKey="jobTitle" />
                  </div>
                </TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Category</TableHead>
                <TableHead 
                  className="text-right cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('totalBidCents')}
                >
                  <div className="flex items-center justify-end">
                    Bid Amount
                    <SortIcon columnKey="totalBidCents" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('outcome')}
                >
                  <div className="flex items-center">
                    Outcome
                    <SortIcon columnKey="outcome" />
                  </div>
                </TableHead>
                <TableHead>Award Reasons</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedBids.map((bid) => (
                <TableRow key={bid.id}>
                  <TableCell className="font-medium">
                    {format(new Date(bid.submittedAt), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <Link 
                      to={`/rfqs/${bid.rfqPublicId}`}
                      className="text-primary hover:underline font-medium"
                    >
                      {bid.jobTitle}
                    </Link>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {bid.rfqPublicId}
                    </div>
                  </TableCell>
                  <TableCell>{bid.clientName}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {getCategoryLabel(bid.jobCategory)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(bid.totalBidCents)}
                    {bid.outcome === 'won' && bid.awardAmountCents && (
                      <div className="text-xs text-green-600">
                        Award: {formatCurrency(bid.awardAmountCents)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <BidOutcomeBadge outcome={bid.outcome} />
                  </TableCell>
                  <TableCell>
                    <SelectionReasonsBadges 
                      reasons={bid.selectionReasons}
                      outcome={bid.outcome}
                      additionalComments={bid.additionalComments}
                    />
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" asChild>
                      <Link to={`/rfqs/${bid.rfqPublicId}`}>
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {sortedBids.map((bid) => (
          <Card key={bid.id}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <Link 
                    to={`/rfqs/${bid.rfqPublicId}`}
                    className="text-primary hover:underline font-medium"
                  >
                    {bid.jobTitle}
                  </Link>
                  <div className="text-sm text-muted-foreground">{bid.clientName}</div>
                </div>
                <BidOutcomeBadge outcome={bid.outcome} />
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                <div>
                  <span className="text-muted-foreground">Date:</span>{' '}
                  {format(new Date(bid.submittedAt), 'MMM d, yyyy')}
                </div>
                <div>
                  <span className="text-muted-foreground">Bid:</span>{' '}
                  <span className="font-medium">{formatCurrency(bid.totalBidCents)}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Badge variant="outline" className="text-xs">
                    {getCategoryLabel(bid.jobCategory)}
                  </Badge>
                </div>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/rfqs/${bid.rfqPublicId}`}>
                    View Details
                  </Link>
                </Button>
              </div>

              {bid.selectionReasons && bid.selectionReasons.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <SelectionReasonsBadges 
                    reasons={bid.selectionReasons}
                    outcome={bid.outcome}
                    additionalComments={bid.additionalComments}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
